/**
 * A member's own account: what's asked of them right now, their delivery
 * photos, the countdown to the first box, and the way back to the box
 * journal. Nothing here is the owner's view of the roster — see
 * /api/v1/csa/members for that — this is only ever what belongs to
 * whoever is signed in.
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { FarmFeed } from '../components/FarmFeed.js';
import { apiGet, apiPost, ApiError } from '../api.js';
import { signOut } from '../auth.js';

interface Member {
  id: string;
  name: string;
  email: string;
  delivery_address: string | null;
  delivery_instructions: string | null;
  phone: string | null;
  box_price_minor: number | null;
  payment_reference: string | null;
  payment_status: 'PENDING' | 'PAID' | 'FREE' | 'PLAN_CURRENT' | 'PAST_DUE' | null;
  payment_mode: 'ANNUAL' | 'INSTALLMENTS' | null;
  payments_completed: number;
  payments_total: number | null;
  boxes_delivered_count: number;
  notify_box_packed: boolean | null;
  notify_box_delivered: boolean | null;
  notify_farm_news: boolean | null;
  notify_alerts: boolean | null;
  next_payment_due_at?: string | null;
}

interface Payment {
  id: string;
  entry_date: string;
  amount_minor: number;
  direction: 'INCOME' | 'EXPENSE';
}

interface PendingTask {
  id: string;
  kind: 'ADDRESS' | 'PAYMENT' | 'REVIEW';
  note: string | null;
  created_at: string;
}

interface Delivery {
  id: string;
  delivered_at: string;
  photo_url: string | null;
}

interface MeResponse {
  /**
   * Set when whoever is signed in works at the farm and is looking at this
   * page rather than living in it. The account details below are invented
   * when this is true — see /member/v1/me — and the API refuses every write
   * from a preview credential, so everything that writes is left out of the
   * page rather than shown as a button that 403s.
   */
  preview?: boolean;
  member: Member;
  banner: string | null;
  pending_tasks: PendingTask[];
  deliveries: Delivery[];
  first_delivery_date: string | null;
  delivery_count: number | null;
  payment_link: string | null;
  /** Show the welcome card: paid, no box yet, not closed. */
  welcome?: boolean;
  season_year?: number | null;
  payments?: Payment[];
}

const money = (minor: number) => `$${(minor / 100).toFixed(2)}`;
const shortDay = (iso: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const TASK_TITLE: Record<PendingTask['kind'], string> = {
  ADDRESS: 'Confirm your delivery address',
  PAYMENT: 'Update your payment details',
  REVIEW: 'Leave a season review',
};

function daysUntil(dateStr: string): number {
  const target = new Date(`${dateStr}T00:00:00`).getTime();
  return Math.ceil((target - Date.now()) / 86_400_000);
}

const WEEK_MS = 7 * 86_400_000;

/**
 * Boxes go out weekly, on the same weekday as the first delivery — so the
 * Nth box is always N-1 weeks after the first. Returns null once every box
 * the program promised has already gone out.
 */
function nextDelivery(firstDeliveryDate: string, deliveryCount: number | null): { date: string; index: number } | null {
  const first = new Date(`${firstDeliveryDate}T00:00:00`).getTime();
  const weeksElapsed = Math.max(0, Math.ceil((Date.now() - first) / WEEK_MS));
  if (deliveryCount != null && weeksElapsed >= deliveryCount) return null;
  const date = new Date(first + weeksElapsed * WEEK_MS).toISOString().slice(0, 10);
  return { date, index: weeksElapsed + 1 };
}

/** A big number, not a stopwatch — what matters is "how many days," and that only needs to update once a day. */
function Countdown({
  firstDeliveryDate,
  deliveryCount,
}: {
  firstDeliveryDate: string;
  deliveryCount: number | null;
}): JSX.Element {
  const daysToFirst = daysUntil(firstDeliveryDate);

  if (daysToFirst >= 0) {
    return (
      <div class="countdown">
        <span class="countdown-number">{daysToFirst === 0 ? 'Today' : daysToFirst}</span>
        <span class="countdown-label">
          {daysToFirst === 0 ? 'is the first box' : `day${daysToFirst === 1 ? '' : 's'} until the first box`}
        </span>
      </div>
    );
  }

  const next = nextDelivery(firstDeliveryDate, deliveryCount);
  if (!next) {
    return (
      <div class="countdown">
        <span class="countdown-number">🎉</span>
        <span class="countdown-label">That was the last box — season complete</span>
      </div>
    );
  }

  const days = daysUntil(next.date);
  return (
    <div class="countdown">
      <span class="countdown-number">{days === 0 ? 'Today' : days}</span>
      <span class="countdown-label">
        {days === 0 ? `is box ${next.index}` : `day${days === 1 ? '' : 's'} until box ${next.index}`}
      </span>
    </div>
  );
}

/**
 * The first thing a new member sees after paying: confirmation that it
 * worked, and a short list of what to do before the first box. Stays until
 * they close it or the first box is delivered.
 */
function WelcomeCard({ data, preview, onClose }: { data: MeResponse; preview: boolean; onClose: () => void }): JSX.Element {
  const { member } = data;
  const [closing, setClosing] = useState(false);
  const paidLine =
    member.payment_status === 'FREE'
      ? 'Your share is confirmed. No charge this season.'
      : member.payment_mode === 'INSTALLMENTS' && member.payments_total
        ? `Payment ${member.payments_completed} of ${member.payments_total} is in.`
        : 'Paid in full.';
  const steps: { done: boolean; title: string; detail: string; href?: string }[] = [
    { done: true, title: 'Your share is confirmed', detail: paidLine },
    {
      done: Boolean(member.delivery_address),
      title: member.delivery_address ? 'Delivery address is set' : 'Tell us where the box goes',
      detail: member.delivery_address ? member.delivery_address.split('\n')[0]! : 'Just below, so box one finds you.',
    },
    { done: false, title: 'Meet the flock', detail: 'The hens and ducks your share helps feed.', href: '/flock' },
    { done: false, title: 'Read the farm log', detail: 'Members-only posts, and first picks on wreaths and specialty pumpkins.', href: '/log' },
  ];
  if (member.payment_mode === 'INSTALLMENTS' && member.next_payment_due_at && member.payment_status === 'PLAN_CURRENT') {
    steps.push({ done: false, title: `Next payment ${shortDay(member.next_payment_due_at)}`, detail: 'Shopify takes it automatically. We’ll email a heads-up a few days before.' });
  }

  async function close() {
    setClosing(true);
    if (!preview) await apiPost('/member/v1/welcome/dismiss', {}).catch(() => undefined);
    onClose();
  }

  return (
    <section class="welcome-card" aria-labelledby="welcome-heading">
      <span class="ff-eyebrow">{data.season_year ? `${data.season_year} season · ` : ''}confirmed</span>
      <h2 id="welcome-heading">You’re in. Welcome to the farm.</h2>
      <p class="welcome-lede">
        {data.first_delivery_date
          ? `Your first box comes ${new Date(`${data.first_delivery_date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}. Until then, here’s what’s waiting for you.`
          : 'Here’s what’s waiting for you before the first box.'}
      </p>
      <ol class="welcome-steps">
        {steps.map((step) => {
          const body = (
            <>
              <span class={`welcome-tick ${step.done ? 'is-done' : ''}`} aria-hidden="true">{step.done ? '✓' : ''}</span>
              <span class="welcome-step-text">
                <strong>{step.title}</strong>
                <span>{step.detail}</span>
              </span>
              {step.href && <span class="welcome-go" aria-hidden="true">→</span>}
            </>
          );
          return (
            <li key={step.title} class={step.done ? 'is-done' : ''}>
              {step.href ? <Link href={step.href}>{body}</Link> : <div>{body}</div>}
            </li>
          );
        })}
      </ol>
      <button class="link" disabled={closing} onClick={() => void close()}>
        Got it, tuck this away
      </button>
    </section>
  );
}

/** Every payment the farm has on its books for this member, and the next one if there is one. */
function PaymentHistory({ payments, member }: { payments: Payment[]; member: Member }): JSX.Element | null {
  const upcoming = member.payment_status === 'PLAN_CURRENT' && member.next_payment_due_at ? member.next_payment_due_at : null;
  if (payments.length === 0 && !upcoming) return null;
  return (
    <ul class="payment-history">
      {payments.map((p) => (
        <li key={p.id}>
          <span>{shortDay(p.entry_date)}</span>
          <span>{p.direction === 'EXPENSE' ? `Refund ${money(p.amount_minor)}` : money(p.amount_minor)}</span>
        </li>
      ))}
      {upcoming && (
        <li class="is-upcoming">
          <span>{shortDay(upcoming)}</span>
          <span>Next payment</span>
        </li>
      )}
    </ul>
  );
}

function TaskForm({ task, onDone }: { task: PendingTask; onDone: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    setBusy(true);
    setError(null);

    try {
      if (task.kind === 'ADDRESS') {
        await apiPost('/member/v1/details', { delivery_address: String(data.get('delivery_address') ?? '') });
      } else if (task.kind === 'PAYMENT') {
        throw new ApiError('Payment status is managed through Shopify. Contact the farm if something looks wrong.', 409);
      } else {
        const rating = Number(data.get('rating') ?? 0) || undefined;
        await apiPost('/member/v1/review', { rating, body: String(data.get('body') ?? '') });
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="card" onSubmit={(event) => void submit(event)} id={`task-${task.kind.toLowerCase()}`}>
      <span class="card-title">{TASK_TITLE[task.kind]}</span>
      {task.note && <p class="card-sub">{task.note}</p>}

      {task.kind === 'ADDRESS' && (
        <label>
          Delivery address
          <textarea name="delivery_address" required />
        </label>
      )}
      {task.kind === 'PAYMENT' && (
        <p class="card-sub">
          Payment records come directly from Shopify. If this task is still showing after checkout, contact the farm.
        </p>
      )}
      {task.kind === 'REVIEW' && (
        <>
          <label>
            Rating, out of 5
            <input name="rating" type="number" min="1" max="5" inputMode="numeric" />
          </label>
          <label>
            A few words
            <textarea name="body" />
          </label>
        </>
      )}

      {error && <p class="error" role="alert">{error}</p>}
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}

/** The way to reach the farm for anything that isn't self-service yet — an address change, a question, whatever comes up mid-season. */
function ContactForm(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost('/member/v1/help', { message });
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <section class="card">
        <span class="card-title">Message sent</span>
        <span class="card-sub">The farm will get back to you.</span>
        <button class="link" onClick={() => setSent(false)}>
          Send another
        </button>
      </section>
    );
  }

  if (!open) {
    return (
      <button class="link" onClick={() => setOpen(true)}>
        Need to change something? Contact the farm
      </button>
    );
  }

  return (
    <form class="card" onSubmit={(event) => void send(event)}>
      <span class="card-title">Contact the farm</span>
      <label>
        What's going on?
        <textarea
          value={message}
          onInput={(event) => setMessage((event.target as HTMLTextAreaElement).value)}
          required
        />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <div class="form-actions">
        <button class="primary" type="submit" disabled={busy || !message.trim()}>
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button class="link" type="button" onClick={() => setOpen(false)}>
          Never mind
        </button>
      </div>
    </form>
  );
}

/**
 * A member asking for their data, or asking to be deleted — routed to
 * staff rather than automated, since both need a human to check first: an
 * export shouldn't leak into the wrong inbox, and a delete shouldn't erase
 * a mid-season delivery record someone still needs. This just puts the ask
 * in front of the farm; see /member/v1/data-request.
 */
function DataRequestForm(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'EXPORT' | 'DELETE'>('EXPORT');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost('/member/v1/data-request', { kind, note: note.trim() || undefined });
      setSent(true);
      setNote('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <section class="card">
        <span class="card-title">Request sent</span>
        <span class="card-sub">The farm will follow up with you directly.</span>
        <button class="link" onClick={() => setSent(false)}>
          Send another
        </button>
      </section>
    );
  }

  if (!open) {
    return (
      <button class="link" onClick={() => setOpen(true)}>
        Request my data, or ask to delete it
      </button>
    );
  }

  return (
    <form class="card" onSubmit={(event) => void submit(event)}>
      <span class="card-title">Data request</span>
      <label>
        What do you need?
        <select value={kind} onChange={(event) => setKind(event.currentTarget.value as 'EXPORT' | 'DELETE')}>
          <option value="EXPORT">A copy of what the farm has on file for me</option>
          <option value="DELETE">Delete my data</option>
        </select>
      </label>
      <label>
        Anything to add? <span class="small muted">(optional)</span>
        <textarea value={note} onInput={(event) => setNote((event.target as HTMLTextAreaElement).value)} />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <div class="form-actions">
        <button class="primary" type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button class="link" type="button" onClick={() => setOpen(false)}>
          Never mind
        </button>
      </div>
    </form>
  );
}

/** Onboarding, not a data request — asked once, right after accepting, so a box has somewhere to go. */
function DetailsForm({ onDone }: { onDone: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await apiPost('/member/v1/details', {
        delivery_address: String(data.get('delivery_address') ?? ''),
        delivery_instructions: String(data.get('delivery_instructions') ?? ''),
        phone: String(data.get('phone') ?? ''),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="card card-accent" onSubmit={(event) => void submit(event)}>
      <span class="card-title">Where does the box go?</span>
      <span class="card-sub">Needed before the first delivery — nothing else about your account depends on it.</span>
      <label>
        Delivery address
        <textarea name="delivery_address" required />
      </label>
      <label>
        Delivery instructions <span class="small muted">(optional — "leave by the blue door")</span>
        <input name="delivery_instructions" />
      </label>
      <label>
        Phone <span class="small muted">(optional)</span>
        <input name="phone" type="tel" />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}

/** Editing details after setup, not just the one-time onboarding ask — the address form re-opened, prefilled with what's on file. */
function EditDetails({ member, onDone }: { member: Member; onDone: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button class="link" onClick={() => setOpen(true)}>
        Edit
      </button>
    );
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await apiPost('/member/v1/details', {
        delivery_address: String(data.get('delivery_address') ?? ''),
        delivery_instructions: String(data.get('delivery_instructions') ?? ''),
        phone: String(data.get('phone') ?? ''),
      });
      setOpen(false);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="stack" onSubmit={(event) => void submit(event)}>
      <label>
        Delivery address
        <textarea name="delivery_address" required value={member.delivery_address ?? ''} />
      </label>
      <label>
        Delivery instructions <span class="small muted">(optional)</span>
        <input name="delivery_instructions" value={member.delivery_instructions ?? ''} />
      </label>
      <label>
        Phone <span class="small muted">(optional)</span>
        <input name="phone" type="tel" value={member.phone ?? ''} />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <div class="form-actions">
        <button class="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button class="link" type="button" onClick={() => setOpen(false)}>
          Never mind
        </button>
      </div>
    </form>
  );
}

const NOTIFICATION_TOGGLES: Array<{
  key: 'notify_box_packed' | 'notify_box_delivered' | 'notify_farm_news' | 'notify_alerts';
  label: string;
  hint: string;
}> = [
  { key: 'notify_box_packed', label: 'Box packed', hint: "A heads-up on Friday or Saturday that this week's box is ready and coming Sunday." },
  { key: 'notify_box_delivered', label: 'Box delivered', hint: 'A photo of your box the moment it lands on the porch.' },
  { key: 'notify_farm_news', label: 'Farm news', hint: "Routine updates — what's happening on the farm, nothing urgent." },
  { key: 'notify_alerts', label: 'Alerts', hint: 'Time-sensitive notices — a delivery delay, a season change.' },
];

interface ProductOrder {
  id: string;
  state: string;
  offer_id: string;
  product_name: string;
  offer_state: string;
  price_minor: string | null;
  short_code: string | null;
  lines: Array<{ id: string; quantity: string; status: string }>;
}

interface FlockUpdate {
  event_type: string;
  caption: string | null;
  animal_slug: string | null;
  animal_name: string | null;
  photo_url: string | null;
}

/** A small strip next to the countdown — whatever the flock has been up to lately, each card opening onto that animal's own page. Hidden entirely when there is nothing to show. */
function FlockStrip(): JSX.Element | null {
  const [updates, setUpdates] = useState<FlockUpdate[] | null>(null);

  useEffect(() => {
    apiGet<{ updates: FlockUpdate[] }>('/member/v1/flock/updates?limit=4')
      .then((result) => setUpdates(result.updates))
      .catch(() => setUpdates([]));
  }, []);

  if (!updates || updates.length === 0) return null;

  return (
    <section class="card">
      <div class="card-heading">
        <span class="card-title">From the flock</span>
        <Link href="/flock" class="card-sub">
          See everyone →
        </Link>
      </div>
      <div class="photo-grid">
        {updates.map((update, index) => {
          const name = update.animal_name ?? 'Unnamed';
          const href = update.animal_slug ? `/flock/${encodeURIComponent(update.animal_slug)}` : '/flock';
          return (
            <Link key={index} href={href} style="display: block;">
              {update.photo_url && <img src={update.photo_url} alt={name} loading="lazy" />}
              <span class="card-sub" style="display: block; margin-top: 0.3rem;">
                {name}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/** Every preorder a member has made, across every offer — a wreath scanned in week 12, salsa never appears here since it's never preordered. */
function ProductOrders(): JSX.Element | null {
  const [orders, setOrders] = useState<ProductOrder[] | null>(null);

  useEffect(() => {
    apiGet<{ orders: ProductOrder[] }>('/member/v1/products/orders')
      .then((result) => setOrders(result.orders))
      .catch(() => setOrders([]));
  }, []);

  if (!orders || orders.length === 0) return null;

  return (
    <section class="card">
      <span class="card-title">Your preorders</span>
      {orders.map((order) => {
        const label = (
          <>
            {order.product_name} · {order.lines[0]?.quantity ?? '—'}
            {order.state === 'CANCELLED' ? ' (cancelled)' : order.offer_state !== 'PREORDER_OPEN' ? ' (closed)' : ''}
          </>
        );
        return order.short_code ? (
          <Link key={order.id} href={`/p/${order.short_code}`} class="card-sub" style="display: block;">
            {label}
          </Link>
        ) : (
          <span key={order.id} class="card-sub" style="display: block;">
            {label}
          </span>
        );
      })}
    </section>
  );
}

/** A missing key ({@code null}) reads as opted in — every toggle here defaults on until a member turns it off themselves. */
function NotificationPreferences({ member, onDone }: { member: Member; onDone: () => void }): JSX.Element {
  const [saving, setSaving] = useState<string | null>(null);

  async function toggle(key: (typeof NOTIFICATION_TOGGLES)[number]['key'], checked: boolean): Promise<void> {
    setSaving(key);
    try {
      await apiPost('/member/v1/notification-preferences', { [key]: checked });
      onDone();
    } catch {
      // Best-effort from the member's point of view too — a failed save just
      // means the checkbox reverts next load, not a scary error banner over
      // an emailing preference.
    } finally {
      setSaving(null);
    }
  }

  return (
    <section class="card">
      <span class="card-title">Emails</span>
      <span class="card-sub">What we'll email you about — turn off anything you don't want.</span>
      {NOTIFICATION_TOGGLES.map(({ key, label, hint }) => (
        <label key={key} class="check">
          <input
            type="checkbox"
            checked={member[key] !== false}
            disabled={saving === key}
            onChange={(event) => void toggle(key, (event.target as HTMLInputElement).checked)}
          />
          {label}
          <span class="card-sub small muted" style="display: block; margin-left: 1.6rem;">
            {hint}
          </span>
        </label>
      ))}
    </section>
  );
}

export function AccountView(): JSX.Element {
  const [data, setData] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);

  const welcome = new URLSearchParams(window.location.search).get('welcome') === '1';

  const load = () => {
    apiGet<MeResponse>('/member/v1/me')
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load your account.'));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!data) return;
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (!focus) return;
    document.getElementById(`task-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [data]);

  async function cancel(): Promise<void> {
    setBusy(true);
    try {
      await apiPost('/member/v1/cancel', {});
      await signOut('/box');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
      setBusy(false);
    }
  }

  if (error) {
    return (
      <main class="view centred">
        <h1>Your account couldn't load.</h1>
        <p class="error" role="alert">{error}</p>
        <button class="primary" onClick={() => { setError(null); load(); }}>Try again</button>
        <Link href="/login?next=%2Faccount">Sign in again</Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Loading…</p>
      </main>
    );
  }

  const { member, banner, pending_tasks, deliveries, first_delivery_date, delivery_count, payment_link } = data;
  const preview = data.preview === true;
  const needsDetails = !preview && !member.delivery_address;

  return (
    <main class="view account-view">
      {preview && (
        <div class="banner">
          <strong>Preview.</strong> This is the member area as a member sees it. The name, address and
          payment below are made up — no member's details are shown here — and nothing on this page can
          be changed while you're previewing.
        </div>
      )}

      {welcome && !data.welcome && (
        <div class="banner">
          <strong>Welcome aboard.</strong> Glad you're doing this with us.
        </div>
      )}

      <header class="account-heading">
        <div><span class="ff-eyebrow">Your season with us</span>
        <h1>Hi, {member.name.trim().split(/\s+/)[0] || member.name}</h1>
        <p class="muted">
          {member.boxes_delivered_count} box{member.boxes_delivered_count === 1 ? '' : 'es'} delivered so far
        </p>
      </div>
      {first_delivery_date && <Countdown firstDeliveryDate={first_delivery_date} deliveryCount={delivery_count} />}
      </header>

      {banner && <div class="banner">{banner}</div>}
      <div class="account-columns">
      <div class="account-main">
      {data.welcome && <WelcomeCard data={data} preview={preview} onClose={() => setData({ ...data, welcome: false })} />}
      <div class="account-tasks">
        {needsDetails && <DetailsForm onDone={load} />}
        {pending_tasks.map((task) => <TaskForm key={task.id} task={task} onDone={load} />)}
      </div>
      <div class="member-card-grid">
        <Link href="/box" class="card member-destination">
          <span class="card-title">What's in the box</span>
          <span class="card-sub">This week's box, and every one before it.</span>
        </Link>
      </div>

      {!preview && <ProductOrders />}

      {deliveries.length > 0 && (
        <section class="card">
          <span class="card-title">Your deliveries</span>
          <div class="photo-grid">
            {deliveries
              .filter((d) => d.photo_url)
              .map((d) => (
                <figure key={d.id}><img src={d.photo_url!} alt="Your delivered box" loading="lazy" /><figcaption>{new Date(d.delivered_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</figcaption></figure>
              ))}
          </div>
        </section>
      )}

      <section class="account-feed" aria-labelledby="feed-heading">
        <div class="account-feed-head">
          <h2 id="feed-heading">From the farm</h2>
          <Link href="/log">The whole farm log →</Link>
        </div>
        <FarmFeed limit={5} compact />
      </section>

      <FlockStrip />
      </div>
      <aside class="account-settings" aria-label="Account settings">
      <h2>Your membership</h2>
      <div class="member-card-grid settings-grid">
      <section class="card">
        <div class="card-heading">
          <span class="card-title">Payment</span>
        </div>
        {member.payment_status === 'FREE' && <span class="card-sub">This season is free — you're all set.</span>}
        {member.payment_status === 'PAID' && <span class="card-sub">Paid in full — you’re all set.</span>}
        {member.payment_status === 'PLAN_CURRENT' && (
          <span class="card-sub">
            Payment plan current
            {member.payments_total ? ` · ${member.payments_completed} of ${member.payments_total} payments received` : ''}
          </span>
        )}
        {member.payment_status === 'PAST_DUE' && (
          <span class="card-sub">Your latest monthly payment hasn't come through. Check the card on file with the farm shop, or reply to any farm email and we'll sort it out.</span>
        )}
        {member.payment_status === 'PENDING' && (
          <>
            <span class="card-sub">Enrollment payment is still pending.</span>
            {payment_link && (
              <a class="primary" href={payment_link} target="_blank" rel="noreferrer">
                Open farm shop
              </a>
            )}
          </>
        )}
        {member.payment_mode && (
          <span class="card-sub muted">
            {member.payment_mode === 'INSTALLMENTS' ? 'Four monthly payments' : 'Paid annually'}
          </span>
        )}
        <PaymentHistory payments={data.payments ?? []} member={member} />
        {member.payment_reference && <span class="card-sub muted">Shopify reference: {member.payment_reference}</span>}
      </section>

      <section class="card">
        <div class="card-heading">
          <span class="card-title">Details</span>
          {!preview && <EditDetails member={member} onDone={load} />}
        </div>
        <span class="card-sub">{member.email}</span>
        {member.delivery_address && <span class="card-sub">{member.delivery_address}</span>}
        {member.delivery_instructions && <span class="card-sub">{member.delivery_instructions}</span>}
        {member.phone && <span class="card-sub">{member.phone}</span>}
      </section>
      </div>

      {!preview && (
        <>
          <NotificationPreferences member={member} onDone={load} />
          <ContactForm />
          <DataRequestForm />
        </>
      )}

      <button class="link" onClick={() => void signOut()}>
        {preview ? 'End preview' : 'Sign out'}
      </button>

      {preview ? null : cancelling ? (
        <div class="card card-danger">
          <span class="card-title">Cancel your share?</span>
          <span class="card-sub">This frees your spot for the next person on the waitlist and cannot be undone here.</span>
          <div class="form-actions">
            <button class="link danger" disabled={busy} onClick={() => void cancel()}>
              {busy ? 'Working…' : 'Yes, cancel my share'}
            </button>
            <button class="link" onClick={() => setCancelling(false)}>
              Never mind
            </button>
          </div>
        </div>
      ) : (
        <button class="link danger" onClick={() => setCancelling(true)}>
          Cancel my share
        </button>
      )}
      </aside>
      </div>
    </main>
  );
}
